import { useState, useEffect, useRef, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface PipelineStep { name: string; command: string; continueOnError?: boolean }
interface PipelineConfig { name: string; trigger: string; steps: PipelineStep[] }
interface StepResult { name: string; status: string; duration: number; output: string }
interface RunRecord { id: string; pipelineName: string; status: string; steps: StepResult[]; startedAt: string; finishedAt?: string }

export function CICDPanel() {
  const [tab, setTab] = useState<'run' | 'history' | 'config'>('run')
  const [pipelines, setPipelines] = useState<PipelineConfig[]>([])
  const [runs, setRuns] = useState<RunRecord[]>([])
  const [selectedPipeline, setSelectedPipeline] = useState('')
  const [running, setRunning] = useState(false)
  const [runId, setRunId] = useState<string | null>(null)
  const [stepStatuses, setStepStatuses] = useState<{ name: string; status: string; duration?: number }[]>([])
  const [logs, setLogs] = useState<string[]>([])
  const [finalStatus, setFinalStatus] = useState<string | null>(null)
  const [editIdx, setEditIdx] = useState<number | null>(null)
  const logsRef = useRef<HTMLDivElement>(null)

  // Load pipelines and runs
  useEffect(() => {
    authFetch(apiUrl('/api/pipeline')).then(r => r.json()).then(data => {
      setPipelines(data)
      if (data.length && !selectedPipeline) setSelectedPipeline(data[0].name)
    }).catch(() => {})
    authFetch(apiUrl('/api/pipeline/runs')).then(r => r.json()).then(setRuns).catch(() => {})
  }, [])

  useEffect(() => { logsRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [logs])

  const triggerRun = useCallback(async () => {
    if (!selectedPipeline || running) return
    setRunning(true)
    setLogs([])
    setFinalStatus(null)
    setStepStatuses([])
    setTab('run')

    try {
      const res = await authFetch(apiUrl('/api/pipeline/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: selectedPipeline }),
      })
      if (!res.body) { setRunning(false); return }

      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'start') {
              setRunId(data.runId)
              setStepStatuses(data.steps.map((s: string) => ({ name: s, status: 'pending' })))
            }
            if (data.type === 'step-start') {
              setStepStatuses(prev => prev.map((s, i) => i === data.step ? { ...s, status: 'running' } : s))
            }
            if (data.type === 'log') {
              setLogs(prev => [...prev, data.text])
            }
            if (data.type === 'step-done') {
              setStepStatuses(prev => prev.map((s, i) => i === data.step ? { ...s, status: data.status, duration: data.duration } : s))
            }
            if (data.type === 'done') {
              setFinalStatus(data.status)
              // Refresh runs
              authFetch(apiUrl('/api/pipeline/runs')).then(r => r.json()).then(setRuns).catch(() => {})
            }
          } catch {}
        }
      }
    } catch {} finally {
      setRunning(false)
    }
  }, [selectedPipeline, running])

  const cancelRun = async () => {
    if (runId) await authFetch(apiUrl('/api/pipeline/cancel'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ runId }) }).catch(() => {})
  }

  const savePipelines = async (updated: PipelineConfig[]) => {
    setPipelines(updated)
    await authFetch(apiUrl('/api/pipeline'), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated) }).catch(() => {})
  }

  const addPipeline = () => {
    const name = `Pipeline ${pipelines.length + 1}`
    savePipelines([...pipelines, { name, trigger: 'manual', steps: [{ name: 'Build', command: 'npm run build' }] }])
    setSelectedPipeline(name)
    setEditIdx(pipelines.length)
  }

  const deletePipeline = (idx: number) => {
    const updated = pipelines.filter((_, i) => i !== idx)
    savePipelines(updated)
    if (selectedPipeline === pipelines[idx].name && updated.length) setSelectedPipeline(updated[0].name)
    setEditIdx(null)
  }

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <span>CI / CD</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['run', 'history', 'config'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
              border: tab === t ? '1px solid var(--accent)' : '1px solid var(--border-2)',
              background: tab === t ? 'rgba(124,110,245,0.1)' : 'none',
              color: tab === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {/* Run tab */}
      {tab === 'run' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Controls */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <select value={selectedPipeline} onChange={e => setSelectedPipeline(e.target.value)} style={inputStyle}>
                {pipelines.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
              {running ? (
                <button onClick={cancelRun} style={{ ...btnStyle, borderColor: 'var(--red)', color: 'var(--red)' }}>Cancel</button>
              ) : (
                <button onClick={triggerRun} disabled={!selectedPipeline} style={{ ...btnStyle, borderColor: 'var(--accent-2)', color: 'var(--accent-2)' }}>▶ Run</button>
              )}
            </div>
          </div>

          {/* Step progress */}
          {stepStatuses.length > 0 && (
            <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              {stepStatuses.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                  <StepIcon status={s.status} />
                  <span style={{ flex: 1, color: s.status === 'running' ? 'var(--text-0)' : 'var(--text-1)' }}>{s.name}</span>
                  {s.duration !== undefined && <span style={{ fontSize: 9, color: 'var(--text-2)' }}>{formatDuration(s.duration)}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Logs */}
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px', background: 'var(--bg-0)', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent-2)', lineHeight: 1.6 }}>
            {logs.length === 0 && !finalStatus && (
              <div style={{ color: 'var(--text-2)', textAlign: 'center', padding: 20, fontSize: 11 }}>
                Select a pipeline and click ▶ Run
              </div>
            )}
            {logs.map((l, i) => <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{l}</div>)}
            {finalStatus && (
              <div style={{ marginTop: 8, padding: 8, borderRadius: 6, border: `1px solid ${finalStatus === 'passed' ? 'var(--accent-2)' : 'var(--red)'}`, background: finalStatus === 'passed' ? 'rgba(95,212,164,0.08)' : 'rgba(240,112,112,0.08)' }}>
                <span style={{ fontWeight: 700, color: finalStatus === 'passed' ? 'var(--accent-2)' : 'var(--red)' }}>
                  {finalStatus === 'passed' ? '✓ Pipeline passed' : finalStatus === 'cancelled' ? '⊘ Cancelled' : '✗ Pipeline failed'}
                </span>
              </div>
            )}
            <div ref={logsRef} />
          </div>
        </div>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          {runs.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>No runs yet</div>
          ) : runs.map(r => (
            <div key={r.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: r.status === 'passed' ? 'var(--accent-2)' : r.status === 'failed' ? 'var(--red)' : 'var(--text-2)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-0)', fontWeight: 600 }}>{r.pipelineName}</div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                  {r.status} · {new Date(r.startedAt).toLocaleString()} · {r.id.slice(0, 8)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                {r.steps.map((s, i) => (
                  <span key={i} title={`${s.name}: ${s.status}`} style={{ width: 6, height: 6, borderRadius: 2, background: s.status === 'passed' ? 'var(--accent-2)' : s.status === 'failed' ? 'var(--red)' : 'var(--bg-4)' }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Config tab */}
      {tab === 'config' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
          {pipelines.map((p, pi) => (
            <div key={pi} style={{ marginBottom: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-2)', cursor: 'pointer' }} onClick={() => setEditIdx(editIdx === pi ? null : pi)}>
                <span style={{ fontSize: 9, color: 'var(--text-2)' }}>{editIdx === pi ? '▾' : '▸'}</span>
                <input
                  value={p.name}
                  onChange={e => { const u = [...pipelines]; u[pi] = { ...u[pi], name: e.target.value }; setPipelines(u) }}
                  onBlur={() => savePipelines(pipelines)}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'none', border: 'none', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, flex: 1, outline: 'none' }}
                />
                <button onClick={(e) => { e.stopPropagation(); deletePipeline(pi) }} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 10 }} title="Delete">✕</button>
              </div>
              {editIdx === pi && (
                <div style={{ padding: '8px 10px' }}>
                  {p.steps.map((s, si) => (
                    <div key={si} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                      <input value={s.name} onChange={e => { const u = [...pipelines]; u[pi].steps[si].name = e.target.value; setPipelines(u) }} onBlur={() => savePipelines(pipelines)} placeholder="Step name" style={{ ...inputStyle, width: 80 }} />
                      <input value={s.command} onChange={e => { const u = [...pipelines]; u[pi].steps[si].command = e.target.value; setPipelines(u) }} onBlur={() => savePipelines(pipelines)} placeholder="command" style={inputStyle} />
                      <button onClick={() => { const u = [...pipelines]; u[pi].steps.splice(si, 1); savePipelines(u) }} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9 }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => { const u = [...pipelines]; u[pi].steps.push({ name: 'Step', command: '' }); savePipelines(u) }} style={{ fontSize: 9, color: 'var(--accent)', background: 'none', border: '1px dashed var(--border-2)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>+ Add step</button>
                </div>
              )}
            </div>
          ))}
          <button onClick={addPipeline} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px dashed var(--border-2)', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }}>+ New Pipeline</button>
        </div>
      )}
    </div>
  )
}

function StepIcon({ status }: { status: string }) {
  const s: React.CSSProperties = { width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }
  if (status === 'passed') return <span style={{ ...s, color: 'var(--accent-2)' }}>✓</span>
  if (status === 'failed') return <span style={{ ...s, color: 'var(--red)' }}>✗</span>
  if (status === 'running') return <span style={{ ...s, color: 'var(--accent-3)', animation: 'pulse-heartbeat 1.4s infinite' }}>●</span>
  if (status === 'skipped') return <span style={{ ...s, color: 'var(--text-2)' }}>○</span>
  return <span style={{ ...s, color: 'var(--text-2)' }}>○</span>
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const inputStyle: React.CSSProperties = { flex: 1, padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const btnStyle: React.CSSProperties = { padding: '5px 12px', borderRadius: 4, border: '1px solid', background: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700 }
